/**
 * Body view tree — the rendering-pipeline shape consumed by `flatten`
 * and `CellLayout.Map.make`. Resolves projection rows (D2) by hoisting
 * their children onto the dedicated header / footer surface anchor
 * rows (any `SurfaceAnchor` shape — see `./surface-anchor.ts`).
 *
 * This module is the rendering bridge between authored structure and
 * displayed structure:
 *
 *   Authored tree:
 *     header                              ← system or server-backed
 *     calendar [alias]
 *       projection [surface=header]       ← structural relocator
 *         timeline[row]
 *           calendar-list
 *       timeline[column]
 *         calendar-list
 *     <body rows...>
 *
 *   Body view tree (post-derivation):
 *     header
 *       <header's authored children>
 *       timeline[row]                     ← hoisted from projection
 *         calendar-list
 *     calendar [alias]
 *       timeline[column]                  ← projection container dropped
 *         calendar-list
 *     <body rows...>
 *
 * The projection mechanism (PR 1 / PR 2) stays as the authoring shape;
 * `BodyViewTree.derive` interprets it for rendering. SDK consumers
 * (plugins, formula, `ProjectionContext`) keep walking the authored
 * tree via `row.children`; only the rendering pipeline sees the view
 * tree.
 *
 * D6 hide path: a projection with `attributes.visible === false`
 * suppresses its children entirely (handled by
 * `SurfaceProjection.project` — they don't reach the surface bucket).
 *
 * Relationship to `SurfaceProjection.project`: that helper produces
 * three separate `Surface<Row>` buckets keyed by target. The body
 * view tree grafts the header / footer buckets onto the *children
 * of the dedicated header / footer rows in body*, producing one
 * unified shape consumers can walk top-down.
 */

import type { Option } from "../core/option.ts";
import { Json } from "../core/json.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { InertRow } from "../rows/variants/inert-row.ts";
import { SurfaceAnchor } from "./surface-anchor.ts";
import { projectionVisible, SurfaceProjection } from "./projection.ts";
import type { SurfaceKind } from "../backends/rows-backend.ts";

/**
 * Derives the displayed row tree from the authored one, hoisting projection rows' children onto the
 * header and footer surfaces, for flattening and layout.
 */
export namespace BodyViewTree {
  /**
   * What `derive` needs to know about the input row type. Extends
   * `SurfaceProjection.Deps` with header / footer recognizers — the
   * dedicated surface rows are the hoist targets.
   */
  export interface Deps<Row> extends SurfaceProjection.Deps<Row> {
    /** True for the top-level header surface anchor row. */
    readonly isHeader: (row: Row) => boolean;
    /** True for the top-level footer surface anchor row. */
    readonly isFooter: (row: Row) => boolean;
  }

  /**
   * View-tree wrapper. Preserves the original row identity but
   * exposes a derived `children()` that includes any hoisted
   * projection children (for header / footer rows) and excludes
   * dropped projection containers (everywhere).
   */
  export interface View<Row> {
    readonly row: Row;
    children(): readonly View<Row>[];
  }

  /**
   * Pure derivation: walk the authored top-level rows, run
   * surface projection, and return a view tree where header /
   * footer rows host both their authored and hoisted children.
   * Top-level projection rows are dropped (their children fold
   * into the appropriate surface).
   *
   * Hoisted rows render as **leaves** — their descendants are
   * subsumed in the view tree so the host (e.g., a hoisted
   * `timeline[row]`) appears as a single visible block in the
   * surface area. Plugin discovery (which uses authored
   * `row.children` directly, not the view tree) still finds the
   * descendants for content-provider lookup.
   */
  export function derive<Row>(
    topLevelRows: readonly Row[],
    deps: Deps<Row>,
  ): readonly View<Row>[] {
    const projected = SurfaceProjection.project(topLevelRows, deps);

    // Hoisted rows render as leaves. Their descendants render
    // through the host's plugin (timeline[row] → all-day strip
    // events; etc.), not as siblings in flat output.
    const hoistedRowSet = new Set<Row>();
    for (const node of projected.header.children) hoistedRowSet.add(node.row);
    for (const node of projected.footer.children) hoistedRowSet.add(node.row);

    // Map from row identity → augmented children list. Only the FIRST
    // header / footer row per kind gets an augmentation (one canonical
    // anchor per surface — duplicates would double-render hoisted
    // children); everything else uses its projected (already-stripped)
    // children verbatim.
    const augmented = new Map<Row, readonly SurfaceProjection.Node<Row>[]>();
    let headerGrafted = false;
    let footerGrafted = false;
    for (const node of projected.body.children) {
      if (
        !headerGrafted &&
        deps.isHeader(node.row) &&
        projected.header.children.length > 0
      ) {
        augmented.set(node.row, [...node.children, ...projected.header.children]);
        headerGrafted = true;
      } else if (
        !footerGrafted &&
        deps.isFooter(node.row) &&
        projected.footer.children.length > 0
      ) {
        // Footer surface: hoisted projection content (e.g. a chart's
        // x-axis timeline-legend) renders ABOVE the always-bottom system
        // chrome (ephemeral disclaimer, sheet-footer). So the hoisted
        // children come first; the authored system rows trail at the
        // bottom. (Header keeps authored-first order — the calendar
        // all-day strip hoists below the authored header rows.)
        augmented.set(node.row, [...projected.footer.children, ...node.children]);
        footerGrafted = true;
      }
    }

    // Convert each projected body node into a View<Row>, augmenting
    // header / footer with their hoisted entries. Children memoized
    // per-node so consumers can call `view.children()` repeatedly
    // without allocating each time.
    function buildView(node: SurfaceProjection.Node<Row>): View<Row> {
      // Hoisted rows expose no children in the view tree (they
      // render as a single leaf-block via the host's plugin).
      const kids: readonly SurfaceProjection.Node<Row>[] =
        hoistedRowSet.has(node.row)
          ? []
          : augmented.get(node.row) ?? node.children;
      let cached: Option<readonly View<Row>[]> = null;
      return {
        row: node.row,
        children: () => cached ??= kids.map(buildView),
      };
    }

    return projected.body.children.map(buildView);
  }

  // ── ModelRow specialization ───────────────────────────────────────

  const SHEET_ROW_DEPS: Deps<ModelRow> = {
    childrenOf: (row) => row.children,
    isProjection: (row) =>
      row instanceof InertRow && row.descriptor.kind === "projection",
    // Hoist target predicate. Matches any `SurfaceAnchor` shape:
    // the server-backed `surface-container` (`meta.surface`), an
    // authored `surface[header|footer]` region row
    // (`attributes.surface`), or — legacy fallback — the synthetic
    // `InertRow` of kind `"header"` / `"sheet-footer"` for sheets
    // that pre-date the surface-container wrapping (only needed for
    // the brief window before the system rows rebuild on first
    // load).
    isHeader: (row) => surfaceKindOf(row) === "header",
    isFooter: (row) => surfaceKindOf(row) === "footer",
    attributesOf: (row) => {
      const attrs = row.attributes();
      const validSurface = Json.oneOf(attrs, "surface", SurfaceProjection.TARGETS);
      const position = Json.number(attrs, "position");
      // Timescale-hidden, parent-decided or own `visible` — see `projectionVisible`.
      return { surface: validSurface, position, visible: projectionVisible(row, attrs) };
    },
  };

  /** ModelRow specialization of `derive`. */
  export function deriveSheet(
    topLevelRows: readonly ModelRow[],
  ): readonly View<ModelRow>[] {
    return derive(topLevelRows, SHEET_ROW_DEPS);
  }

  /**
   * Resolve the surface kind of a `ModelRow`. Delegates to
   * `SurfaceAnchor.of`, which recognizes all three anchor shapes —
   * server-backed surface-container (`meta.surface`), authored
   * surface region (`attributes.surface = "header" | "footer"`),
   * and the legacy synthetic `header` / `sheet-footer` system row.
   * Returns `null` for non-surface rows (including bare/body
   * surfaces).
   */
  function surfaceKindOf(row: ModelRow): Option<SurfaceKind> {
    return SurfaceAnchor.of(row)?.kind ?? null;
  }
}
